import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Play, RefreshCw, LogOut } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api";
import { useI18n, localeSort } from "@/i18n";
import JobLogsTable from "@/components/JobLogsTable";
import AdminNav from "@/components/AdminNav";
import BulkBar, { RowCheckbox, SelectAllBox, useSelection } from "@/components/BulkBar";
import PromoCodesPanel from "@/components/PromoCodesPanel";
import FeedbackPanel from "@/components/FeedbackPanel";
import { useDeployGuard } from "@/components/DeployGuard";
import PublicCountersPanel from "@/components/PublicCountersPanel";
import AnomaliesPanel from "@/components/AnomaliesPanel";
import GuardsPanel from "@/components/GuardsPanel";
import ReleaseGatePanel from "@/components/ReleaseGatePanel";

const JOB_NAMES = ["sync_teams", "sync_players", "sync_players_teams", "sync_milestones", "sync_upcoming_fixtures", "backfill_match_sheets", "sync_news", "compute_signals", "sync_club_logos", "checklist_discovery", "process_notifications", "reconcile_billing", "migrate_eligibility", "apply_rookie_criterion", "dedupe_cards", "retention_purge", "reclassify_rookies", "career_window_guard", "contributor_expiry", "sync_transfers", "refresh_ages", "recompute_milestones", "rescan_restored_players", "purge_self_club_changes", "backfill_usage_names"];

function StatCard({ label, value, testid }) {
  return (
    <div className="hud-card hud-card-hover rounded-xl p-4" data-testid={testid}>
      <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-1 font-display text-3xl font-bold text-[#00F0FF]">{value}</p>
    </div>
  );
}

function ThresholdRow({ t }) {
  const [val, setVal] = useState("");
  const [saved, setSaved] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    api.get("/admin/rookie-threshold").then((r) => { setVal(String(r.data.value)); setSaved(r.data.value); }).catch(() => {});
  }, []);
  const n = parseInt(val, 10);
  const doPreview = async () => {
    try {
      const r = await api.get("/admin/rookie-threshold/preview", { params: { n } });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const doSave = async () => {
    try {
      await api.put("/admin/rookie-threshold", { value: n });
      setSaved(n);
      toast.success(t("admin.threshold_saved"));
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="mb-4 rounded-lg border border-[#1E2638] bg-[#07090E]/60 p-3" data-testid="threshold-row">
      <div className="flex flex-wrap items-center gap-2">
        <label className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.rookie_threshold")}</label>
        <input type="number" min="1" max="500" value={val} onChange={(e) => setVal(e.target.value)} data-testid="threshold-input"
          className="w-20 rounded-md border border-[#1F293D] bg-[#0B0E14] px-2 py-1 font-mono2 text-xs text-slate-200" />
        <button onClick={doPreview} disabled={!n} data-testid="threshold-preview-btn" className="rounded-md border border-[#00F0FF]/40 px-2 py-1 font-mono2 text-[10px] uppercase text-[#00F0FF] hover:bg-[#00F0FF]/10">{t("admin.threshold_preview")}</button>
        <button onClick={doSave} disabled={!n || n === saved} data-testid="threshold-save-btn" className="rounded-md border border-[#00FF87]/40 px-2 py-1 font-mono2 text-[10px] uppercase text-[#00FF87] hover:bg-[#00FF87]/10">{t("admin.threshold_save")}</button>
      </div>
      {preview && (
        <p className="mt-2 font-mono2 text-[10px] text-slate-400" data-testid="threshold-preview-result">
          N={preview.n} → rookie {preview.counts.rookie} · {preview.transitions.would_graduate} {t("admin.preview_would_graduate")} · {preview.transitions.would_promote} {t("admin.preview_would_promote")} · {preview.counts.unknown} unknown
          <span className="block text-slate-600">{preview.note}</span>
        </p>
      )}
    </div>
  );
}

function SeasonRow({ t }) {
  const [val, setVal] = useState("");
  const [saved, setSaved] = useState(null);
  const [precheck, setPrecheck] = useState(null);
  const [pcRunning, setPcRunning] = useState(false);
  useEffect(() => {
    api.get("/admin/api-season").then((r) => { setVal(String(r.data.value)); setSaved(r.data.value); }).catch(() => {});
  }, []);
  const n = parseInt(val, 10);
  const doSave = async () => {
    try {
      const r = await api.put("/admin/api-season", { value: n });
      setSaved(n);
      toast.success(t("admin.api_season_saved"));
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  return (
    <div className="mb-4 rounded-lg border border-[#1E2638] bg-[#07090E]/60 p-3" data-testid="api-season-row">
      <div className="flex flex-wrap items-center gap-2">
        <label className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.api_season")}</label>
        <input type="number" min="2020" max="2100" value={val} onChange={(e) => setVal(e.target.value)} data-testid="api-season-input"
          className="w-24 rounded-md border border-[#1F293D] bg-[#0B0E14] px-2 py-1 font-mono2 text-xs text-slate-200" />
        <button onClick={doSave} disabled={!n || n === saved} data-testid="api-season-save-btn" className="rounded-md border border-[#00FF87]/40 px-2 py-1 font-mono2 text-[10px] uppercase text-[#00FF87] hover:bg-[#00FF87]/10">{t("admin.threshold_save")}</button>
      </div>
      <p className="mt-2 font-mono2 text-[10px] text-slate-600">{t("admin.api_season_hint")}</p>
      <button onClick={async () => {
        setPcRunning(true);
        try {
          const r = await api.post("/admin/pro-precheck");
          setPrecheck(r.data);
          if (r.data.all_pass) { toast.success(r.data.verdict); } else { toast.error(r.data.verdict); }
        } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
        finally { setPcRunning(false); }
      }} disabled={pcRunning} data-testid="pro-precheck-btn"
        className="mt-2 rounded-md border border-[#00F0FF]/40 px-2 py-1 font-mono2 text-[10px] uppercase text-[#00F0FF] hover:bg-[#00F0FF]/10 disabled:opacity-40">
        {pcRunning ? t("admin.pro_precheck_running") : t("admin.pro_precheck")}
      </button>
      {precheck && (
        <div className="mt-2 space-y-1" data-testid="pro-precheck-result">
          {precheck.checks.map((c) => (
            <p key={c.id} className={`font-mono2 text-[10px] ${c.pass ? "text-[#00FF87]" : "text-red-400"}`} data-testid={`pro-precheck-${c.id}`}>
              {c.pass ? "PASS" : "FAIL"} · {c.id} · {c.detail} · {JSON.stringify(c.numbers)}
            </p>
          ))}
          <p className="font-mono2 text-[10px] font-bold text-slate-200" data-testid="pro-precheck-verdict">
            {precheck.verdict} · budget used: {precheck.budget_used_by_precheck}
          </p>
        </div>
      )}
    </div>
  );
}


function SuspectAppearancesPanel({ t }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/suspect-appearances").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  const rescan = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/players/flag-suspect-appearances");
      toast.success(`${r.data.flagged} ${t("admin.suspect_flagged")} · ${r.data.minutes_removed} min`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const evidence = async (row) => {
    try {
      const r = await api.get(`/admin/suspect-appearances/${row.fixture_id}/${row.player_id}/evidence`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `api-football-evidence-${row.fixture_id}-${row.player_id}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(t("admin.suspect_evidence_downloaded"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!data) return null;
  return (
    <section className="mb-6 rounded-xl p-5 hud-card" data-testid="admin-suspect-appearances-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-lg font-bold uppercase tracking-tight text-slate-100">{t("admin.suspect_appearances")}</h2>
        <span className="font-mono2 text-[10px] text-slate-400">
          <b className={data.suspect ? "text-orange-400" : "text-[#00FF87]"} data-testid="suspect-appearances-count">{data.suspect}</b> / {data.total_appearances} ({data.share_pct}%) · <b className="text-[#FFD600]" data-testid="suspect-minutes-excluded">{data.minutes_excluded}</b> {t("admin.suspect_minutes")} · <span data-testid="suspect-with-evidence">{data.with_evidence}</span> {t("admin.suspect_with_evidence")}
        </span>
        <button type="button" onClick={rescan} disabled={busy} className="ml-auto border border-[#1F293D] px-2.5 py-1 font-mono2 text-[10px] uppercase text-slate-300 hover:border-[#00F0FF] disabled:opacity-40" data-testid="admin-rescan-suspect-btn">
          {t("admin.suspect_rescan")}
        </button>
      </div>
      <p className="mt-1 font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{t("admin.suspect_hint")}</p>
      {data.suspect > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[520px] font-mono2 text-[10.5px]">
            <thead className="text-[9px] uppercase tracking-widest text-slate-500"><tr className="text-left"><th className="py-1">{t("admin.stale_clubs_player")}</th><th>{t("admin.stale_clubs_record")}</th><th>{t("admin.suspect_match")}</th><th className="text-right">Min</th><th className="text-right">{t("admin.stale_clubs_date")}</th><th className="text-right">{t("admin.suspect_evidence")}</th></tr></thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={`${r.player_id}-${i}`} className="border-t border-[#1F293D]" data-testid={`suspect-row-${r.player_id}`}>
                  <td className="py-1.5 text-slate-200">{r.name}</td>
                  <td className="text-slate-400">{r.club_name || "—"}</td>
                  <td className="text-orange-400">{r.match}</td>
                  <td className="text-right text-[#FFD600]">{r.minutes ?? "—"}</td>
                  <td className="text-right text-slate-500">{r.date}</td>
                  <td className="text-right">
                    {r.has_evidence ? (
                      <button type="button" onClick={() => evidence(r)} className="border border-[#1F293D] px-1.5 py-0.5 text-[9px] uppercase text-[#00F0FF] hover:border-[#00F0FF]" data-testid={`suspect-evidence-btn-${r.player_id}`}>
                        {t("admin.suspect_evidence_download")}
                      </button>
                    ) : (
                      <span className="text-[9px] uppercase text-slate-600" data-testid={`suspect-no-evidence-${r.player_id}`}>{t("admin.suspect_evidence_missing")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function StaleClubsPanel({ t }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/stale-clubs").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  const repair = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/players/repair-stale-clubs");
      toast.success(`${r.data.fixed} ${t("admin.stale_clubs_repaired")}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (!data) return null;
  return (
    <section className="mb-6 rounded-xl p-5 hud-card" data-testid="admin-stale-clubs-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-lg font-bold uppercase tracking-tight text-slate-100">{t("admin.stale_clubs")}</h2>
        <span className="font-mono2 text-[10px] text-slate-400">
          <b className={data.stale ? "text-orange-400" : "text-[#00FF87]"} data-testid="stale-clubs-count">{data.stale}</b> / {data.checked} · {data.repairable} {t("admin.stale_clubs_repairable")}
        </span>
        <button type="button" onClick={repair} disabled={busy || !data.repairable} className="ml-auto border border-[#1F293D] px-2.5 py-1 font-mono2 text-[10px] uppercase text-slate-300 hover:border-[#00F0FF] disabled:opacity-40" data-testid="admin-repair-stale-clubs-btn">
          {t("admin.stale_clubs_repair")}
        </button>
      </div>
      <p className="mt-1 font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{t("admin.stale_clubs_hint")}</p>
      {data.stale > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[520px] font-mono2 text-[10.5px]">
            <thead className="text-[9px] uppercase tracking-widest text-slate-500"><tr className="text-left"><th className="py-1">{t("admin.stale_clubs_player")}</th><th>{t("admin.stale_clubs_record")}</th><th>{t("admin.stale_clubs_last")}</th><th className="text-right">{t("admin.stale_clubs_date")}</th></tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={`${r.player_id}`} className="border-t border-[#1F293D]" data-testid={`stale-club-row-${r.player_id}`}>
                  <td className="py-1.5 text-slate-200">{r.name}</td>
                  <td className="text-slate-400">{r.club_name || "—"}</td>
                  <td className={r.repairable ? "text-[#FFD600]" : "text-slate-500"}>
                    {r.last_match_club || t("admin.stale_clubs_out_of_scope")}
                  </td>
                  <td className="text-right text-slate-500">{r.last_match_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MilestoneHistoryPanel({ t }) {
  // The wrong dates are in PRODUCTION, and the repair script needs a shell: this button is
  // the only way the fix reaches the rows visitors actually see.
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/milestones/club-history").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);
  const repair = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/milestones/repair-club-history");
      toast.success(`${r.data.match_milestones_repaired} + ${r.data.club_changes_repaired} ${t("admin.milestone_repaired")}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  if (!data) return null;
  const pending = data.match_milestones_repaired + data.club_changes_repaired;
  return (
    <section className="mb-6 rounded-xl p-5 hud-card" data-testid="admin-milestone-history-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-lg font-bold uppercase tracking-tight text-slate-100">{t("admin.milestone_history")}</h2>
        <span className="font-mono2 text-[10px] text-slate-400">
          <b className={pending ? "text-orange-400" : "text-[#00FF87]"} data-testid="milestone-history-pending">{pending}</b> · {data.club_changes_repaired} {t("admin.milestone_changes")} · {data.match_milestones_repaired} {t("admin.milestone_clubs")}
        </span>
        <button type="button" onClick={repair} disabled={busy || !pending} className="ml-auto border border-[#1F293D] px-2.5 py-1 font-mono2 text-[10px] uppercase text-slate-300 hover:border-[#00F0FF] disabled:opacity-40" data-testid="admin-repair-milestone-history-btn">
          {t("admin.milestone_repair")}
        </button>
      </div>
      <p className="mt-1 font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{t("admin.milestone_history_hint")}</p>
    </section>
  );
}

function NameReviewPanel({ t }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({});
  const load = useCallback(() => {
    api.get("/admin/players/name-review").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(load, [load]);
  const save = async (p) => {
    const name = (edit[p.player_id] || "").trim();
    if (name.length < 2) return;
    try {
      await api.patch(`/admin/players/${p.provider}/${p.player_id}/display-name`, { name });
      toast.success(`${p.name} → ${name}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };
  if (!data) return null;
  return (
    <section className="mb-6 rounded-xl p-5 hud-card" data-testid="admin-name-review-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-lg font-bold uppercase tracking-tight text-slate-100">{t("admin.name_review")}</h2>
        <span className="font-mono2 text-[10px] text-slate-400" data-testid="name-review-total">
          {data.total} · {data.corrected} {t("admin.name_review_corrected")}
        </span>
      </div>
      <p className="mt-1 font-mono2 text-[10px] uppercase tracking-wider text-slate-500">{t("admin.name_review_note")}</p>
      <div className="mt-3 max-h-[420px] overflow-auto">
        <table className="w-full min-w-[640px] font-mono2 text-[10.5px]">
          <thead className="text-[9px] uppercase tracking-widest text-slate-500">
            <tr className="text-left"><th className="py-1">{t("admin.name_displayed")}</th><th>{t("admin.name_provider")}</th><th>{t("admin.name_club")}</th><th className="text-right">{t("admin.name_fix")}</th></tr>
          </thead>
          <tbody>
            {data.players.map((p) => (
              <tr key={`${p.provider}-${p.player_id}`} className="border-t border-[#1F293D]" data-testid={`name-review-row-${p.player_id}`}>
                <td className="py-1.5 text-slate-200">
                  {p.name}
                  {p.display_name_source === "admin" && <span className="ml-2 text-[#00FF87]">✓</span>}
                </td>
                <td className="text-slate-500">{p.provider_name || "—"}</td>
                <td className="text-slate-400">{p.club_name || "—"}</td>
                <td className="py-1 text-right">
                  <input
                    value={edit[p.player_id] ?? ""}
                    onChange={(e) => setEdit((s) => ({ ...s, [p.player_id]: e.target.value }))}
                    placeholder={p.name}
                    data-testid={`name-review-input-${p.player_id}`}
                    className="w-40 border border-[#1F293D] bg-[#0B0E14] px-2 py-1 text-slate-200"
                  />
                  <button type="button" onClick={() => save(p)} data-testid={`name-review-save-${p.player_id}`}
                          className="ml-2 border border-[#1F293D] px-2 py-1 uppercase text-slate-300 hover:border-[#00FF87] hover:text-[#00FF87]">
                    {t("admin.name_save")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}


function RcGapPanel({ t }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [fix, setFix] = useState(null);
  useEffect(() => { api.get("/admin/rc-gap").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  const backfill = async () => {
    try { const r = await api.post("/admin/players/backfill-names"); setFix(r.data); toast.success(`${r.data.fixed} fixed · ${r.data.still_abbreviated} still abbreviated`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const revoke = async (cardId) => {
    try {
      await api.post("/admin/rc-gap/revoke-transfer-link", { card_id: cardId });
      const r = await api.get("/admin/rc-gap");
      setData(r.data);
      toast.success(t("admin.rc_revoke_done"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!data) return null;
  return (
    <section className="rounded-xl p-5 hud-card" data-testid="admin-rc-gap-panel">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-lg font-bold uppercase tracking-tight text-slate-100">RC gap · checklist ↔ tracker</h2>
        <span className="font-mono2 text-[10px] text-slate-400">RC {data.rc_total} · <b className="text-orange-400" data-testid="rc-gap-unresolved-total">{data.unresolved_total}</b> unresolved</span>
        <button type="button" onClick={backfill} className="ml-auto border border-[#1F293D] px-2.5 py-1 font-mono2 text-[10px] uppercase text-slate-300 hover:border-[#00F0FF]" data-testid="admin-backfill-names-btn">
          {t("admin.backfill_names")}{fix ? ` · ${fix.fixed}/${fix.fixed + fix.still_abbreviated}` : ""}
        </button>
      </div>
      <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[520px] font-mono2 text-[10.5px]">
        <thead className="text-[9px] uppercase tracking-widest text-slate-500"><tr className="text-left"><th className="py-1">Set</th><th className="text-right">RC linked</th><th className="text-right">Pending eligibility</th><th className="text-right">Unresolved</th></tr></thead>
        <tbody>
          {data.sets.map((s) => (
            <Fragment key={s.slug}>
              <tr className="cursor-pointer border-t border-[#1F293D] hover:bg-[#0B0E14]" onClick={() => setOpen(open === s.slug ? null : s.slug)} data-testid={`rc-gap-row-${s.slug}`}>
                <td className="py-1.5 text-slate-200">{s.set_name}</td>
                <td className="text-right text-[#00FF87]" data-testid={`rc-gap-linked-${s.slug}`}>{s.resolved}/{s.rc_total}</td>
                <td className="text-right text-slate-400" data-testid={`rc-gap-pending-${s.slug}`}>{s.resolved_pending_eligibility ?? 0}</td>
                <td className="text-right text-orange-400" data-testid={`rc-gap-unresolved-${s.slug}`}>{s.unresolved}</td>
              </tr>
              {open === s.slug && (
                <tr>
                  <td colSpan={4} className="pb-2 text-slate-400" data-testid={`rc-gap-names-${s.slug}`}>
                    <div className="mb-1 flex flex-wrap gap-2" data-testid={`rc-gap-reasons-${s.slug}`}>
                      {Object.entries(s.by_reason || {}).map(([r, n]) => (
                        <span key={r} className="border border-[#1F293D] px-1.5 py-0.5 text-[9.5px] uppercase text-slate-300">{t(`admin.rc_reason_${r}`)} · {n}</span>
                      ))}
                    </div>
                    {s.unresolved_names.map((u) => `#${u.card_number} ${u.name}${u.team ? ` (${u.team})` : ""} — ${t(`admin.rc_reason_${u.reason}`)}`).join(" · ") || "—"}
                    {(s.transfer_links || []).length > 0 && (
                      <div className="mt-2 border-t border-[#1F293D] pt-2" data-testid={`rc-gap-transfer-links-${s.slug}`}>
                        <div className="mb-1 text-[9px] uppercase tracking-widest text-[#FFD600]">{t("admin.rc_transfer_links")} · {s.transfer_links.length}</div>
                        {s.transfer_links.map((l) => (
                          <div key={l.card_id} className="flex flex-wrap items-center gap-2 py-0.5" data-testid={`rc-transfer-link-${l.card_id}`}>
                            <span className="text-slate-300">#{l.card_number} {l.name}</span>
                            <span className="text-slate-500">{l.card_club || "—"} → {l.player_name} ({l.player_club || "—"})</span>
                            <button type="button" onClick={() => revoke(l.card_id)} className="border border-[#1F293D] px-1.5 py-0.5 text-[9px] uppercase text-slate-400 hover:border-orange-400 hover:text-orange-400" data-testid={`rc-revoke-btn-${l.card_id}`}>
                              {t("admin.rc_revoke_link")}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
      </div>
    </section>
  );
}

function VatChangesPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/vat-country-changes").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data || !data.changes.length) return null;
  return (
    <section className="mb-6 rounded-xl p-5 hud-card" data-testid="admin-vat-changes-panel">
      <h2 className="font-display text-lg font-bold uppercase tracking-tight text-slate-100">
        Tax country changes <span className="font-mono2 text-xs text-orange-400" data-testid="vat-to-review-count">{data.to_review} to review</span>
      </h2>
      <p className="mt-1 font-mono2 text-[9px] uppercase tracking-wider text-slate-500">Declared country changed on an active subscription — VAT is never recomputed silently.</p>
      <table className="mt-3 w-full font-mono2 text-[10.5px]">
        <thead className="text-[9px] uppercase tracking-widest text-slate-500"><tr className="text-left"><th className="py-1">Account</th><th>From</th><th>To</th><th>Subscription</th><th className="text-right">At</th></tr></thead>
        <tbody>
          {data.changes.map((c, i) => (
            <tr key={i} className="border-t border-[#1F293D]" data-testid={`vat-change-row-${i}`}>
              <td className="py-1.5 text-slate-200">{c.email}</td>
              <td className="text-slate-400">{c.from_country}</td>
              <td className="text-[#FFD600]">{c.to_country}</td>
              <td className="truncate text-slate-500">{c.subscription_id || "—"}</td>
              <td className="text-right text-slate-400">{(c.at || "").slice(0, 16).replace("T", " ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function SourcesPanel({ t }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/sources").then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return null;
  const color = { ok: "text-[#00FF87]", error: "text-red-400", rate_limited: "text-orange-400", never_called: "text-slate-500" };
  return (
    <section className="rounded-xl p-5 hud-card" data-testid="admin-sources-panel">
      <h2 className="font-display text-lg font-bold uppercase tracking-tight text-slate-100">{t("admin.sources_title")}</h2>
      <table className="mt-3 w-full font-mono2 text-[10.5px]">
        <thead className="text-[9px] uppercase tracking-widest text-slate-500"><tr className="text-left"><th className="py-1">{t("admin.provider")}</th><th>{t("admin.src_feeds")}</th><th>{t("admin.src_last_ok")}</th><th className="text-right">{t("admin.src_today")}</th><th className="text-right">{t("admin.src_quota")}</th><th className="text-right">{t("admin.src_status")}</th></tr></thead>
        <tbody>
          {data.sources.map((s) => (
            <tr key={s.provider} className="border-t border-[#1F293D]" data-testid={`source-row-${s.provider}`}>
              <td className="py-1.5 text-slate-200">{s.provider}</td>
              <td className="text-slate-400">{s.feeds}</td>
              <td className="text-slate-400" title={s.last_error || ""}>{s.last_ok_at ? s.last_ok_at.slice(0, 16).replace("T", " ") : "—"}</td>
              <td className="text-right text-slate-300">{s.calls_today}{s.errors_today ? <span className="text-red-400"> · {s.errors_today} err</span> : null}{s.rate_limited_today ? <span className="text-orange-400"> · {s.rate_limited_today} 429</span> : null}</td>
              <td className="text-right text-slate-300">{s.quota ? `${s.quota.used}/${s.quota.limit}` : "—"}</td>
              <td className={`text-right uppercase ${color[s.status] || ""}`} data-testid={`source-status-${s.provider}`}>{s.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function EligibilityPanel({ t }) {
  const [data, setData] = useState(null);
  const psel = useSelection();
  const [bulkBusy, setBulkBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const [list, sum] = await Promise.all([
        api.get("/admin/players", { params: { status: "legacy_fd", limit: 50 } }),
        api.get("/admin/players/eligibility/summary"),
      ]);
      setData({ ...list.data, summary: sum.data });
    } catch { /* panel stays hidden on error */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const erase = async (p) => {
    const reason = window.prompt(t("admin.erase_reason"));
    if (!reason) return;
    if (!window.confirm(t("admin.erase_confirm"))) return;
    try {
      await api.post("/admin/players/erase", { provider: p.provider, player_id: p.player_id, reason });
      toast.success(t("admin.erased_ok"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const pkey = (p) => `${p.provider}:${p.player_id}`;

  const bulkPlayers = async (action) => {
    if (bulkBusy) return;  // guard against multi-click double submission
    setBulkBusy(true);
    try {
      const targets = [...psel.sel].map((k) => {
        const [provider, pid] = k.split(":");
        return { provider, player_id: Number(pid) };
      });
      const r = await api.post("/admin/players/bulk", { targets, action });
      toast.success(`${t("admin.action_ok")}: ${r.data.done}${r.data.failed ? ` · ${r.data.failed} ✗` : ""}`);
      psel.clear();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBulkBusy(false); }
  };

  if (!data) return null;
  const c = data.counts || {};
  const mig = data.summary?.last_migration;
  return (
    <div className="mb-6 rounded-xl p-5 hud-card" data-testid="eligibility-panel">
      <h2 className="mb-3 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("admin.elig_title")}</h2>
      <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 font-mono2 text-xs text-slate-400">
        <span data-testid="elig-count-watching">{t("admin.elig_watching")}: <b className="text-[#00FF87]">{c.watching || 0}</b></span>
        <span data-testid="elig-count-unverified">{t("admin.elig_unverified")}: <b className="text-orange-400">{c.unverified || 0}</b></span>
        <span data-testid="elig-count-legacy-fd">{t("admin.elig_legacy_fd")}: <b className="text-[#FFD600]">{c.legacy_fd || 0}</b></span>
        <span data-testid="elig-count-graduated">{t("admin.elig_graduated")}: <b className="text-slate-300">{c.graduated || 0}</b></span>
        <span data-testid="elig-migration">
          {t("admin.elig_migration")}: {mig ? `${mig.moved_to_candidate} ${t("admin.elig_moved")} · ${mig.watching} ${t("admin.elig_remain")}` : t("admin.elig_never")}
        </span>
      </div>
      <ThresholdRow t={t} />
      <SeasonRow t={t} />
      <h3 className="mb-2 font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{t("admin.cand_title")}</h3>
      <BulkBar
        count={psel.count}
        onClear={psel.clear}
        t={t}
        actions={[
          { key: "watch", label: t("admin.bulk_watch"), cls: "border-[#00FF87]/50 text-[#00FF87] hover:bg-[#00FF87]/10", onClick: () => bulkPlayers("watching") },
          { key: "graduate", label: t("admin.bulk_graduate"), cls: "border-slate-500/50 text-slate-300 hover:bg-slate-500/10", onClick: () => bulkPlayers("graduated") },
          { key: "delete", label: t("admin.bulk_delete"), cls: "border-red-500/50 text-red-400 hover:bg-red-500/10", onClick: () => bulkPlayers("delete") },
        ]}
      />
      {data.players.length > 0 && (
        <div className="mb-2 flex items-center gap-2">
          <SelectAllBox
            testid="candidates-select-all"
            checked={data.players.every((p) => psel.sel.has(pkey(p)))}
            indeterminate={!data.players.every((p) => psel.sel.has(pkey(p))) && data.players.some((p) => psel.sel.has(pkey(p)))}
            onChange={(on) => psel.setAll(data.players.map(pkey), on)}
          />
          <span className="font-mono2 text-[10px] uppercase tracking-wider text-slate-500">{t("admin.select_all")}</span>
        </div>
      )}
      {data.players.length === 0 && <p className="text-xs text-slate-500" data-testid="candidates-empty">{t("admin.cand_empty")}</p>}
      <div className="space-y-1.5">
        {data.players.map((p) => (
          <div key={`${p.provider}:${p.player_id}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-[#1E2638] bg-[#07090E]/60 px-3 py-2" data-testid={`candidate-row-${p.player_id}`}>
            <RowCheckbox selected={psel.sel.has(pkey(p))} onToggle={() => psel.toggle(pkey(p))} testid={`candidate-select-${p.player_id}`} />
            <span className="min-w-0 flex-1 truncate text-sm text-slate-200">{p.name} <span className="text-xs text-slate-500">· {p.club_name || "—"} · {p.provider}</span></span>
            <span className="font-mono2 text-[10px] uppercase text-slate-500">{p.eligibility?.evidence_type || "—"}</span>
            <button data-testid={`erase-player-${p.player_id}`} onClick={() => erase(p)} className="rounded-md border border-red-500/40 px-2 py-1 font-mono2 text-[10px] uppercase text-red-400 hover:bg-red-500/10">{t("admin.erase")}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [leagueSel, setLeagueSel] = useState({});
  const guard = useDeployGuard();
  const providerBusy = !!guard?.provider_job_running;
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([api.get("/admin/status"), api.get("/admin/jobs/logs")]);
      setStatus(s.data);
      setLogs(l.data.logs);
    } catch (e) {
      if ([401, 404].includes(e?.response?.status)) navigate("/admin/login");
      else setForbidden(true);
    }
  }, [navigate, api]);

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  const runJob = async (job) => {
    try {
      await api.post("/admin/jobs/run", { job, league_id: leagueSel[job] || null });
      toast.success(t("admin.queued"));
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const stopJob = async (log) => {
    try {
      await api.post("/admin/jobs/stop", { log_id: log.id });
      toast.success(t("admin.stop_requested"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (forbidden) return <p className="pt-16 text-center text-sm text-red-400">Forbidden</p>;
  if (!status) return <p className="pt-16 text-center font-mono2 text-sm text-slate-500">{t("common.loading")}</p>;

  const { provider, usage, counts, sync_state, leagues = [], fd_competitions = [], jobs_meta = {} } = status;
  const selectCls = "rounded-md border border-[#1E2638] bg-[#07090E] px-2 py-2 text-xs text-slate-300 focus:border-[#00FF87] focus:outline-none";

  return (
    <div className="pt-6" data-testid="admin-dashboard">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-black uppercase tracking-tight text-slate-50 sm:text-4xl">{t("admin.dashboard")}</h1>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/cards"
            data-testid="admin-cards-link"
            className="flex items-center gap-1.5 rounded-md border border-[#1F293D] px-3 py-2 font-mono2 text-xs uppercase tracking-wider text-slate-300 hover:border-[#FFD600]/50 hover:text-[#FFD600]"
          >
            {t("cards.admin_title")}
          </Link>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-1.5 rounded-md border border-[#1E2638] px-3 py-2 font-mono2 text-xs uppercase tracking-wider text-slate-300 hover:border-red-500/50 hover:text-red-400"
          >
            <LogOut className="h-3.5 w-3.5" /> {t("admin.logout")}
          </button>
        </div>
      </div>

      <AdminNav />

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <StatCard testid="stat-players" label={t("admin.players_tracked")} value={counts.players} />        <StatCard testid="stat-milestones" label={t("admin.milestones_recorded")} value={counts.milestones} />
        <StatCard testid="stat-signals" label={t("admin.signals")} value={counts.signals} />
        <StatCard testid="stat-fixtures" label={t("admin.fixtures_processed")} value={counts.fixtures_processed} />
        <StatCard testid="stat-teams" label={t("admin.teams")} value={counts.teams} />
        <StatCard testid="stat-usage" label={t("admin.requests_today")} value={usage ? `${usage.used}/${usage.budget}` : "—"} />
      </div>

      <PublicCountersPanel />
      <AnomaliesPanel />
      <ReleaseGatePanel />
      <GuardsPanel />

      <div className="mb-6 rounded-xl p-5 hud-card" data-testid="data-source-card">
        <h2 className="mb-3 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("admin.data_source")}</h2>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-300">
          <span className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${provider.configured ? "bg-[#00FF87] shadow-[0_0_8px_rgba(0,255,135,0.9)]" : "bg-red-500"}`} />
            {provider.name} — {provider.configured ? t("admin.configured") : t("admin.not_configured")}
          </span>
          <span className="font-mono2 text-xs text-slate-500">{provider.base_url}</span>
          <span className="font-mono2 text-xs text-slate-500">
            {t("source.season")} {provider.season} · {provider.leagues_enabled.length} leagues
          </span>
        </div>
        {sync_state.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {sync_state.map((s) => (
              <span key={s.key} className="rounded-full border border-[#1E2638] px-2 py-0.5 font-mono2 text-[10px] text-slate-400" data-testid="sync-state-chip">
                {s.key}: p{s.page}{s.done ? " ✓" : ""}
              </span>
            ))}
          </div>
        )}
      </div>

      <VatChangesPanel />
      <SourcesPanel t={t} />
      <EligibilityPanel t={t} />
      <RcGapPanel t={t} />
      <NameReviewPanel t={t} />
      <StaleClubsPanel t={t} />
      <MilestoneHistoryPanel t={t} />
      <SuspectAppearancesPanel t={t} />

      <FeedbackPanel />

      <PromoCodesPanel />

      <div className="mb-6 rounded-xl p-5 hud-card" data-testid="jobs-panel">
        <h2 className="mb-4 font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("admin.jobs")}</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {JOB_NAMES.map((job) => (
            <div key={job} className="flex flex-col gap-2 rounded-lg border border-[#1E2638] bg-[#07090E]/60 p-3" data-testid={`job-card-${job}`}>
              <span className="font-mono2 text-xs font-bold uppercase tracking-wider text-slate-300">{t(`admin.${job}`)}</span>
              <span className="font-mono2 text-[9px] uppercase tracking-wider text-[#00F0FF]/70" data-testid={`job-source-${job}`}>
                {t("source.label")} : {jobs_meta[job]?.source || t("admin.source_local")}
              </span>
              <p className="text-[10px] leading-snug text-slate-500">{t(`admin.jobdesc.${job}`)}</p>
              {jobs_meta[job]?.league_scope && (
                <select
                  data-testid={`league-select-${job}`}
                  className={selectCls}
                  value={leagueSel[job] || ""}
                  onChange={(e) => setLeagueSel({ ...leagueSel, [job]: e.target.value })}
                >
                  <option value="">{t("admin.all_leagues")}</option>
                  {jobs_meta[job].league_scope === "fd"
                    ? localeSort(fd_competitions, lang, (c) => c.name).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)
                    : localeSort(leagues, lang, (l) => l.name).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
              <button
                data-testid={`run-job-${job}`}
                onClick={() => runJob(job)}
                disabled={providerBusy}
                title={providerBusy ? t("admin.deploy_guard_busy") : undefined}
                className="flex items-center justify-center gap-1.5 rounded-md bg-[#00FF87] px-3 py-2 font-mono2 text-xs font-bold uppercase tracking-wider text-[#07090E] transition-all hover:shadow-[0_0_18px_rgba(0,255,135,0.5)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Play className="h-3 w-3" /> {t("admin.run")}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl p-5 hud-card" data-testid="job-logs-panel">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight text-slate-100">{t("admin.job_logs")}</h2>
          <button data-testid="refresh-logs-button" onClick={load} className="flex items-center gap-1.5 rounded-md border border-[#1E2638] px-2.5 py-1.5 font-mono2 text-xs uppercase tracking-wider text-slate-300 hover:border-[#00F0FF]/50 hover:text-[#00F0FF]">
            <RefreshCw className="h-3 w-3" /> {t("admin.refresh")}
          </button>
        </div>
        <JobLogsTable logs={logs} t={t} onStop={stopJob} />
      </div>
    </div>
  );
}
